import ErrorBoundary from "../../components/ErrorBoundary";
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getSharedFilters, saveSharedFilters } from '../../utils/filterStore';
import axios from 'axios';
import { API_URL as CONFIG_API_URL } from '../../config';
const API_URL = CONFIG_API_URL || 'http://8.141.122.155:3002/api';

import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  RadialLinearScale,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Doughnut, Bar, Radar, Line, Bubble } from 'react-chartjs-2';
import { X, MessageSquare, Target, Activity, FileText, ExternalLink, Check, Filter, ChevronDown, Calendar } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { Download, FileSpreadsheet, Search, Loader2, Bot } from 'lucide-react';
import WordCloudCard from '../../components/WordCloudCard';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  RadialLinearScale,
  Tooltip,
  Legend,
  Filler
);

export default function SeoBrandInsights() {
  const [activeView, setActiveView] = useState("global");
  const [openDropdown, setOpenDropdown] = useState(null);
  const filterBarRef = useRef(null);
  const [projectPersonas, setProjectPersonas] = useState(['全部人设']);
  const [filters, setFilters] = useState(() => getSharedFilters({
    time: '最近 30 天',
    customDate: { start: '', end: '' },
    engines: ['全部搜索平台 (8)'],
    stages: ['全部阶段'],
    keywordTypes: ['全部词汇属性']
  }, 'global_seo_filters'));
  
  useEffect(() => {
    saveSharedFilters(filters, 'global_seo_filters');
  }, [filters]);

  const toggleDropdown = (name) => {
    setOpenDropdown(prev => prev === name ? null : name);
  };

  const toggleArrayFilter = (key, value, defaultAll) => {
    setFilters(prev => {
      let current = [...prev[key]];
      if (value === defaultAll) return { ...prev, [key]: [defaultAll] };
      current = current.filter(v => v !== defaultAll);
      if (current.includes(value)) {
        current = current.filter(v => v !== value);
        if (current.length === 0) current = [defaultAll];
      } else {
        current.push(value);
      }
      return { ...prev, [key]: current };
    });
  };

  const updateTimeFilter = (val) => {
    setFilters(prev => ({ ...prev, time: val }));
    if (val !== '自定义范围') setOpenDropdown(null);
  };

  const renderArrayLabel = (arr, defaultAll, prefix = '') => {
    if (arr.includes(defaultAll)) return `${prefix}${defaultAll}`;
    if (arr.length === 1) return `${prefix}${arr[0]}`;
    return `${prefix}已选 ${arr.length} 项`;
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterBarRef.current && !filterBarRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch Project Personas
  useEffect(() => {
    const fetchPersonas = async () => {
      const token = localStorage.getItem('token');
      const bid = localStorage.getItem('globalBrand');
      let combinedPersonas = ['全部人设'];
      try {
        const sysRes = await axios.get(`${API_URL}/personas/system`);
        if (sysRes.data && Array.isArray(sysRes.data)) {
           combinedPersonas.push(...sysRes.data.map(p => p.name));
        }
      } catch(error) { console.error("Failed to fetch system personas"); }

      try {
        if (bid && bid !== 'all' && bid !== 'undefined') {
          const brandRes = await axios.get(`${API_URL}/brand-projects/${bid}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (brandRes.data && brandRes.data.data && brandRes.data.data.personas) {
             const brandPersonas = brandRes.data.data.personas.map(p => p.name);
             combinedPersonas.push(...brandPersonas);
          }
        }
      } catch(error) { console.error("Failed to fetch brand personas"); }
      
      setProjectPersonas([...new Set(combinedPersonas)]);
    };
    fetchPersonas();
  }, []);

  const [activeEngineTab, setActiveEngineTab] = useState("all");
  const [activeBubbleTab, setActiveBubbleTab] = useState("品牌词");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerTitle, setDrawerTitle] = useState('');
  const [drawerData, setDrawerData] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Media & Article Type Drawer State
  const [isMediaDrawerOpen, setIsMediaDrawerOpen] = useState(false);
  const [mediaDrawerTitle, setMediaDrawerTitle] = useState('');
  const [mediaDrawerData, setMediaDrawerData] = useState([]);
  const [mediaDrawerLoading, setMediaDrawerLoading] = useState(false);

  // Zhimo Drawer State
  const [isZhimoDrawerOpen, setIsZhimoDrawerOpen] = useState(false);
  const [zhimoCitation, setZhimoCitation] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [zhimoStatuses, setZhimoStatuses] = useState({}); // { id: 'idle'|'generating'|'completed' }
  const [zhimoPlatform, setZhimoPlatform] = useState('GENERAL_NEWS');
  const [zhimoTone, setZhimoTone] = useState('专家科普');
  const [zhimoResults, setZhimoResults] = useState({});
  const [zhimoSelectedSources, setZhimoSelectedSources] = useState([]);


  const openMediaEvidenceDrawer = async (title) => {
    setMediaDrawerTitle(title);
    setIsMediaDrawerOpen(true);
    setMediaDrawerLoading(true);
    
    try {
      const globalProject = localStorage.getItem('globalProject');
      const taskId = (globalProject === 'all' || globalProject === 'undefined' || !globalProject) ? undefined : globalProject;
      const globalBrand = localStorage.getItem('globalBrand');
      const brandId = (globalBrand === 'all' || globalBrand === 'undefined' || !globalBrand) ? undefined : globalBrand;
      
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await axios.get(`${API_URL}/results?system=seo`, {
        headers,
        params: {
          geoTaskId: taskId,
          brandProjectId: brandId,
          limit: 150 // Pull more for media/article type view
        }
      });
      
      if (res.data.success && res.data.data) {
        // Build a flattened list of media sources from the results
        let flatMediaList = [];
        res.data.data.forEach(r => {
           let sourcesList = [];
           if (r.sources) {
             try { 
               const parsed = JSON.parse(r.sources); 
               if (Array.isArray(parsed)) sourcesList = parsed;
               else if (parsed && typeof parsed === 'object') sourcesList = [parsed];
             } catch(e){}
           }
           const brands = r.mentionedBrands ? (typeof r.mentionedBrands==='string' ? JSON.parse(r.mentionedBrands) : r.mentionedBrands) : [];
           const fType = (r.funnelStage || 'Sense').toLowerCase();
           const typeMapping = {
              'sense': '新闻通稿/科普',
              'interest': '评测文章/KOL种草',
              'connection': '用户提问/社区问答',
              'action': '导购比价/成交单',
              'share': '用户口碑/晒单/吐槽',
              // legacy fallback
              'awareness': '新闻通稿/科普',
              'appeal': '评测文章/KOL种草',
              'ask': '用户提问/社区问答',
              'act': '导购比价/成交单'
           };
           const articleType = typeMapping[fType] || '新闻通稿/科普';
           
           sourcesList.forEach(s => {
              let platform = s.platform || s.name || s.mediaName || s.media;
              if (platform) {
                 platform = platform.replace(/-.*/, '').trim();
                 flatMediaList.push({
                   id: `${r.id}-${platform}`,
                   resultId: r.id,
                   engineName: r.engineName,
                   platform: platform,
                   articleType: articleType,
                   title: s.title || r.question,
                   url: s.url || s.link || '#',
                   sentiment: r.sentiment,
                   brands: brands
                 });
              }
           });
        });
        setMediaDrawerData(flatMediaList);
      } else {
        setMediaDrawerData([]);
      }
    } catch (err) {
      console.error("Failed to fetch media drawer data:", err);
      setMediaDrawerData([]);
    } finally {
      setMediaDrawerLoading(false);
    }
  };

  const closeMediaDrawer = () => setIsMediaDrawerOpen(false);

  const handleExportMediaExcel = () => {
    if (!mediaDrawerData || mediaDrawerData.length === 0) return;
    
    const excelData = [
      ['序号', '来源媒体', '内容类型', '引用 AI 引擎', '情感偏向', '提及品牌', '文章标题/触发问题', '来源链接']
    ];
    
    const sentimentMap = { 'positive': '正面', 'neutral': '中立', 'negative': '负面', 'controversial': '争议' };
    
    mediaDrawerData.forEach((row, idx) => {
      excelData.push([
        idx + 1,
        row.platform || '',
        row.articleType || '',
        row.engineName || '',
        sentimentMap[row.sentiment] || '中立',
        Array.isArray(row.brands) ? row.brands.join(', ') : '',
        row.title || '',
        row.url || ''
      ]);
    });
    
    // Using simple CSV generation for quick download (or xlsx library if loaded globally)
    try {
        const ws = XLSX.utils.aoa_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "媒体内容溯源明细");
        XLSX.writeFile(wb, `媒体明细导出_${new Date().getTime()}.xlsx`);
    } catch(e) {
        // Fallback to CSV if XLSX is not imported
        let csvContent = "\uFEFF" + excelData.map(e => e.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `媒体明细导出_${new Date().getTime()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
  };

  const openEvidenceDrawer = async (title, queryParams) => {
    setDrawerTitle(title);
    setIsDrawerOpen(true);
    setDrawerLoading(true);
    
    try {
      const { brand, sentiment, cons, pros } = queryParams;
      const globalProject = localStorage.getItem('globalProject');
      const taskId = (globalProject === 'all' || globalProject === 'undefined' || !globalProject) ? undefined : globalProject;
      const globalBrand = localStorage.getItem('globalBrand');
      const brandId = (globalBrand === 'all' || globalBrand === 'undefined' || !globalBrand) ? undefined : globalBrand;
      
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // We pass the brand and sentiment to filter the API. 
      // If 'cons' is passed, we could theoretically do a full text search, but for now we just filter by brand and negative.
      const fetchSentiment = cons ? 'negative' : (pros ? 'positive' : sentiment);
      
      const res = await axios.get(`${API_URL}/results?system=seo`, {
        headers,
        params: {
          geoTaskId: taskId,
          brandProjectId: brandId,
          brand: brand,
          sentiment: fetchSentiment,
          limit: 15
        }
      });
      
      if (res.data.success && res.data.data) {
        // Map GeoResult to Drawer Format
        let realRecords = res.data.data.map(r => ({
          id: r.id,
          platform: r.engineName,
          topic: r.themeName || r.parentThemeName || '综合',
          sentiment: r.sentiment,
          brands: r.mentionedBrands ? JSON.parse(r.mentionedBrands) : [],
          productCards: r.productCards ? (typeof r.productCards === 'string' ? JSON.parse(r.productCards) : r.productCards) : [],
          xhsBrandDetails: r.xhsBrandDetails || [],
          xhsRawNotes: r.xhsRawNotes || [],
          question: r.question,
          rawResponse: (r.answer || '暂无返回结果').replace(/\>\s*.*商品卡片.*(?:(?:\r?\n)\>.*)*/g, '').trim(),
          sources: r.sources ? JSON.parse(r.sources) : [],
          time: new Date(r.createdAt || Date.now()).toLocaleString()
        }));
        
        // If we clicked on a specific 'cons' (劣势词), try to further filter locally by keyword matching in the answer
        if (cons) {
            const filtered = realRecords.filter(r => r.rawResponse.includes(cons));
            if (filtered.length > 0) {
                realRecords = filtered;
            }
        }
        
        setDrawerData(realRecords);
      } else {
        setDrawerData([]);
      }
    } catch (err) { // eslint-disable-next-line no-unused-vars
      console.error("Failed to fetch drawer data:", err);
      setDrawerData([]);
    } finally {
      setDrawerLoading(false);
    }
  };
  
  const closeDrawer = () => setIsDrawerOpen(false);

  const handleExportImage = async () => {
    const contentElement = document.getElementById('report-content');
    if (!contentElement) return;

    try {
      const canvas = await html2canvas(contentElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: document.documentElement.classList.contains('dark') ? '#0f172a' : '#ffffff'
      });
      
      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = `品牌数据洞察_${new Date().toISOString().slice(0, 10)}.png`;
      link.click();
    } catch (error) {
      console.error('Failed to export image', error);
    }
  };

  const handleExportExcel = () => {
    if (!dashboardData) return;

    // 1. 概览趋势与分布
    // Here we can dump competitorTable which is a summary
    const isMatrix = dashboardData.isMatrixMode;
    const summarySheetData = [
      [isMatrix ? '产品' : '品牌', '总提及数', '提及率(SOV)', '优势词汇 (Pros)', '劣势词汇 (Cons)']
    ];
    (dashboardData.competitorTable || []).forEach(row => {
      summarySheetData.push([
        row.name,
        row.mentions,
        row.sov,
        (row.pros || []).join(', '),
        (row.cons || []).join(', ')
      ]);
    });

    // 2. 情感占比 (from sentimentData)
    const sentimentSheetData = [
      ['品牌/产品', '正面评价', '中性评价', '争议评价', '负面评价']
    ];
    // sentimentData shape: labels: ['A', 'B'], datasets: [{label: '正面', data: [..]}, {label: '负面', data:[..]}]
    if (dashboardData.sentimentData && dashboardData.sentimentData.labels) {
      dashboardData.sentimentData.labels.forEach((label, idx) => {
        const row = [label];
        dashboardData.sentimentData.datasets.forEach(ds => {
          row.push(ds.data[idx]);
        });
        sentimentSheetData.push(row);
      });
    }

    // 3. 业务维度热力图 (Heatmap)
    const heatmapSheetData = [
      ['业务维度', ...(dashboardData.heatmapBrands || [])]
    ];
    (dashboardData.heatmapDimensions || []).forEach(dim => {
      const row = [dim];
      (dashboardData.heatmapBrands || []).forEach(b => {
        row.push(dashboardData.heatmapScores?.[dim]?.[b] || 0);
      });
      heatmapSheetData.push(row);
    });

    const wb = XLSX.utils.book_new();
    const wsSummary = XLSX.utils.aoa_to_sheet(summarySheetData);
    const wsSentiment = XLSX.utils.aoa_to_sheet(sentimentSheetData);
    const wsHeatmap = XLSX.utils.aoa_to_sheet(heatmapSheetData);

    // 4. SOV 趋势数据 (realSovTrend)
    const sovTrendSheetData = [];
    if (dashboardData.realSovTrend && dashboardData.realSovTrend.labels) {
      const headerRow = ['日期', ...dashboardData.realSovTrend.datasets.map(ds => ds.label)];
      sovTrendSheetData.push(headerRow);
      
      dashboardData.realSovTrend.labels.forEach((dateLabel, idx) => {
        const row = [dateLabel];
        dashboardData.realSovTrend.datasets.forEach(ds => {
          row.push(ds.data[idx]);
        });
        sovTrendSheetData.push(row);
      });
    }
    const wsSovTrend = XLSX.utils.aoa_to_sheet(sovTrendSheetData.length > 0 ? sovTrendSheetData : [['暂无数据']]);

    // 5. 竞品 SOV 占比 (sovData)
    const sovShareSheetData = [['品牌/竞品名称', 'SOV 提及率占比(%)']];
    if (dashboardData.sovData && dashboardData.sovData.labels) {
      dashboardData.sovData.labels.forEach((label, idx) => {
        const val = dashboardData.sovData.datasets[0]?.data[idx] || 0;
        sovShareSheetData.push([label, val]);
      });
    }
    const wsSovShare = XLSX.utils.aoa_to_sheet(sovShareSheetData.length > 0 ? sovShareSheetData : [['暂无数据']]);

    XLSX.utils.book_append_sheet(wb, wsSummary, "核心盘点SWOT");
    XLSX.utils.book_append_sheet(wb, wsSovShare, "竞品SOV占比");
    XLSX.utils.book_append_sheet(wb, wsSovTrend, "SOV趋势数据");
    XLSX.utils.book_append_sheet(wb, wsSentiment, "情感分布对比");
    XLSX.utils.book_append_sheet(wb, wsHeatmap, "业务维度得分");

    XLSX.writeFile(wb, `品牌数据洞察_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const renderMentionStatus = (record) => {
    if (record.sentiment === 'positive') return <span className="inline-flex items-center px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-600 text-xs border border-emerald-500/20">🟢 正向评价</span>;
    if (record.sentiment === 'negative') return <span className="inline-flex items-center px-2 py-1 rounded-full bg-rose-500/10 text-rose-600 text-xs border border-rose-500/20">🔴 负向评价</span>;
    if (record.sentiment === 'controversial') return <span className="inline-flex items-center px-2 py-1 rounded-full bg-amber-500/10 text-amber-600 text-xs border border-amber-500/20">🟡 争议/多面</span>;
    return <span className="inline-flex items-center px-2 py-1 rounded-full bg-slate-500/10 text-slate-600 text-xs border border-slate-500/20">⚪ 中性客观</span>;
  };
  const engineLogos = {
  /* INJECTED_MB_PC_LOGOS_v2 */
  '豆包': '/icons/doubao.png',
  '豆包(PC)': '/icons/doubao.png',
  '豆包(MB)': '/icons/doubao.png',
  '豆包（PC）': '/icons/doubao.png',
  '豆包（MB）': '/icons/doubao.png',
  'doubao': '/icons/doubao.png',
  '腾讯元宝(PC)': '/icons/yuanbao.png',
  '腾讯元宝(MB)': '/icons/yuanbao.png',
  '腾讯元宝（PC）': '/icons/yuanbao.png',
  '腾讯元宝（MB）': '/icons/yuanbao.png',
  '通义千问': '/icons/tongyi.png',
  '通义千问(PC)': '/icons/tongyi.png',
  '通义千问(MB)': '/icons/tongyi.png',
  '通义千问（PC）': '/icons/tongyi.png',
  '通义千问（MB）': '/icons/tongyi.png',
  '通义': '/icons/tongyi.png',
  '文心一言': '/icons/wenxin.png',
  '文心一言(PC)': '/icons/wenxin.png',
  '文心一言(MB)': '/icons/wenxin.png',
  'Deepseek': '/icons/deepseek.png',
  'DeepSeek': '/icons/deepseek.png',
  'deepseek': '/icons/deepseek.png',
  'Kimi': '/icons/kimi.png',
  'kimi': '/icons/kimi.png',
  '元宝': '/icons/yuanbao.png',
  '百度AI搜索': '/icons/baidu.png',
  '小红书AI': '/icons/xiaohongshu.png',
  '抖音AI': '/icons/douyin.png',
  '小红书': '/icons/xiaohongshu.png',
  'xhs': '/icons/xiaohongshu.png',
  '微信搜一搜': '/icons/wechat.png',
  '微信': '/icons/wechat.png',
};

  
  
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [benchMain, setBenchMain] = useState('');
  const [benchComp, setBenchComp] = useState('');
  const [benchPersona, setBenchPersona] = useState('所有人群 (全局视图)');
  const [selectedBrands, setSelectedBrands] = useState([]);

  useEffect(() => {
    setBenchMain('');
    setBenchComp('');
    setSelectedBrands([]);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const pid = localStorage.getItem('globalProject');
        const bid = localStorage.getItem('globalBrand');
        
        const res = await axios.get(`${API_URL}/dashboard/insights?system=seo`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            engines: filters.engines.includes('全部搜索平台 (8)') ? 'all' : filters.engines.join(','),
            stages: filters.stages.includes('全部阶段') ? 'all' : filters.stages.join(','),
            keywordTypes: activeView === '产品对标矩阵' ? 'all' : (filters.keywordTypes.includes('全部词汇属性') ? 'all' : filters.keywordTypes.join(',')),
            geoTaskId: (pid === 'all' || pid === 'undefined' || !pid) ? undefined : pid,
            brandProjectId: (bid === 'all' || bid === 'undefined' || !bid) ? undefined : bid,
            viewType: activeView,
            timeRange: filters.time === '自定义时间' || filters.time === '自定义范围' ? 'custom' : filters.time,
            startDate: (() => {
              if (filters.time === '最近 7 天') {
                const start = new Date(); start.setDate(start.getDate() - 7); return start.toISOString();
              } else if (filters.time === '最近 30 天') {
                const start = new Date(); start.setDate(start.getDate() - 30); return start.toISOString();
              } else if (filters.time === '最近 90 天') {
                const start = new Date(); start.setDate(start.getDate() - 90); return start.toISOString();
              } else if (filters.time === '自定义时间' || filters.time === '自定义范围') {
                return filters.customDate.start ? new Date(filters.customDate.start).toISOString() : undefined;
              }
              return undefined;
            })(),
            endDate: (() => {
              if (filters.time === '最近 7 天' || filters.time === '最近 30 天' || filters.time === '最近 90 天') {
                return new Date().toISOString();
              } else if (filters.time === '自定义时间' || filters.time === '自定义范围') {
                if (filters.customDate.end) {
                  const end = new Date(filters.customDate.end);
                  end.setHours(23, 59, 59, 999);
                  return end.toISOString();
                }
              }
              return undefined;
            })()
          }
        });
        
        if (res.data.success && res.data.data) {
          setDashboardData(res.data.data);
        } else {
          setDashboardData(null);
        }
      } catch (e) {
        console.error("Failed to load insights data", e);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    window.addEventListener('globalFilterChange', fetchData);
    return () => window.removeEventListener('globalFilterChange', fetchData);
  }, [activeView, filters]);

  const rawSovData = dashboardData?.sovData || { labels: [], datasets: [] };
  const rawVisData = dashboardData?.visData || null;

  const sovData = useMemo(() => {
    if (!rawSovData.labels || rawSovData.labels.length === 0) return rawSovData;
    if (selectedBrands.length === 0) return rawSovData;
    const indicesToKeep = rawSovData.labels.map((l, idx) => selectedBrands.includes(l.replace(' (本品)', '')) ? idx : -1).filter(idx => idx !== -1);
    return {
       ...rawSovData,
       labels: rawSovData.labels.filter((l) => selectedBrands.includes(l.replace(' (本品)', ''))),
       datasets: rawSovData.datasets.map(ds => ({
          ...ds,
          data: ds.data.filter((_, idx) => indicesToKeep.includes(idx)),
          backgroundColor: Array.isArray(ds.backgroundColor) ? ds.backgroundColor.filter((_, idx) => indicesToKeep.includes(idx)) : ds.backgroundColor,
          borderColor: Array.isArray(ds.borderColor) ? ds.borderColor.filter((_, idx) => indicesToKeep.includes(idx)) : ds.borderColor,
       }))
    };
  }, [rawSovData, selectedBrands]);

  const visData = useMemo(() => {
    if (!rawVisData || !rawVisData.labels || rawVisData.labels.length === 0) return rawVisData;
    if (selectedBrands.length === 0) return rawVisData;
    const indicesToKeep = rawVisData.labels.map((l, idx) => selectedBrands.includes(l.replace(' (本品)', '')) ? idx : -1).filter(idx => idx !== -1);
    return {
       ...rawVisData,
       labels: rawVisData.labels.filter((l) => selectedBrands.includes(l.replace(' (本品)', ''))),
       datasets: rawVisData.datasets.map(ds => ({
          ...ds,
          data: ds.data.filter((_, idx) => indicesToKeep.includes(idx)),
          backgroundColor: Array.isArray(ds.backgroundColor) ? ds.backgroundColor.filter((_, idx) => indicesToKeep.includes(idx)) : ds.backgroundColor,
          borderColor: Array.isArray(ds.borderColor) ? ds.borderColor.filter((_, idx) => indicesToKeep.includes(idx)) : ds.borderColor,
       }))
    };
  }, [rawVisData, selectedBrands]);

  const radarData = dashboardData?.radarData || { labels: [], datasets: [] };
  const sentimentData = dashboardData?.sentimentData || { labels: [], datasets: [] };
  
  const heatmapDimensions = dashboardData?.heatmapDimensions || [];
  const heatmapBrands = dashboardData?.heatmapBrands || [];
  
  const heatmapScores = dashboardData?.heatmapScores || {};

  // Auto-initialize selectedBrands when project/brand changes or data loads
  useEffect(() => {
    if (heatmapBrands.length > 0) {
      // Find the main brand and top competitors (up to 6 total)
      // Usually, heatmapBrands has the main brand first, followed by configured competitors
      const defaultSelection = heatmapBrands.slice(0, 6);
      setSelectedBrands(defaultSelection);
    }
  }, [dashboardData?.brandName, dashboardData?.heatmapBrands?.join(',')]);

  // Apply visual filtering for charts based on selectedBrands
  const displayBrands = selectedBrands.length > 0 
      ? heatmapBrands.filter(b => selectedBrands.includes(b)) 
      : heatmapBrands.slice(0, 6); // fallback

  const filteredRadarData = useMemo(() => {
     if (!dashboardData?.radarData) return { labels: [], datasets: [] };
     const raw = dashboardData.radarData;
     return {
        ...raw,
        datasets: raw.datasets.filter(ds => selectedBrands.includes(ds.label))
     };
  }, [dashboardData, selectedBrands]);

  const filteredSentimentData = useMemo(() => {
     if (!dashboardData?.sentimentData) return { labels: [], datasets: [] };
     const raw = dashboardData.sentimentData;
     const indicesToKeep = raw.labels.map((l, idx) => selectedBrands.includes(l.replace(' (本品)', '')) ? idx : -1).filter(idx => idx !== -1);
     return {
        ...raw,
        labels: raw.labels.filter((l) => selectedBrands.includes(l.replace(' (本品)', ''))),
        datasets: raw.datasets.map(ds => ({
           ...ds,
           data: ds.data.filter((_, idx) => indicesToKeep.includes(idx))
        }))
     };
  }, [dashboardData, selectedBrands]);

  const seoRadarData = {
    ...filteredRadarData,
    labels: filteredRadarData.labels || []
  };

  const chartSentimentData = selectedBrands.length > 0 ? filteredSentimentData : sentimentData;
  const chartRadarData = selectedBrands.length > 0 ? seoRadarData : { ...radarData, labels: radarData.labels || [] };

  // The Bar chart uses sovData.labels for brand names, and sovData.datasets[0].backgroundColor array for their colors.

  const hasRealData = sovData?.labels?.length > 0;
  const defaultBrands = ['马牌', '米其林', '普利司通', '固特异', '倍耐力'];
  const defaultColors = [
    'rgba(41, 150, 207, 0.7)',
    'rgba(234, 188, 107, 0.7)',
    'rgba(148, 163, 184, 0.7)',
    'rgba(96, 170, 132, 0.7)',
    'rgba(245, 102, 109, 0.7)'
  ];
  
  const brandsList = hasRealData ? sovData.labels : defaultBrands;
  let colorsList = defaultColors;
  
  if (hasRealData && sovData.datasets && sovData.datasets.length > 0) {
    const bgColors = sovData.datasets[0].backgroundColor;
    if (Array.isArray(bgColors)) {
      colorsList = bgColors;
    } else if (typeof bgColors === 'string') {
      colorsList = Array(brandsList.length).fill(bgColors);
    }
  }
  
  const getBorderColor = (bg) => {
    if (!bg) return 'rgba(41, 150, 207, 1)';
    if (Array.isArray(bg)) bg = bg[0];
    if (typeof bg === 'string') {
      if (bg.startsWith('#')) return bg;
      if (bg.startsWith('rgba')) return bg.replace(/[^,]+(?=\))/, '1');
      return bg;
    }
    return 'rgba(41, 150, 207, 1)';
  };

  const getBubbleBgColor = (bg) => {
    if (!bg) return 'rgba(41, 150, 207, 0.6)';
    if (Array.isArray(bg)) bg = bg[0];
    if (typeof bg === 'string') {
      if (bg.startsWith('#')) {
         const r = parseInt(bg.slice(1, 3), 16) || 0;
         const g = parseInt(bg.slice(3, 5), 16) || 0;
         const b = parseInt(bg.slice(5, 7), 16) || 0;
         return `rgba(${r}, ${g}, ${b}, 0.6)`;
      }
      if (bg.startsWith('rgba')) return bg.replace(/[^,]+(?=\))/, '0.6');
      return bg;
    }
    return 'rgba(41, 150, 207, 0.6)';
  };

  const competitorTable = dashboardData?.competitorTable || [];
  const productMappings = dashboardData?.productMappings || [];
  const isMatrixMode = dashboardData?.isMatrixMode || false;
  const wordCloudData = dashboardData?.wordCloudData || {};

  const interceptionData = useMemo(() => {
    const raw = dashboardData?.interceptionData;
    if (!raw || !raw.labels) return { labels: [], datasets: [] };
    if (selectedBrands.length === 0) return raw;
    const indicesToKeep = raw.labels.map((l, idx) => selectedBrands.includes(l) ? idx : -1).filter(idx => idx !== -1);
    return {
       ...raw,
       labels: raw.labels.filter(l => selectedBrands.includes(l)),
       datasets: raw.datasets.map(ds => ({
          ...ds,
          data: ds.data.filter((_, idx) => indicesToKeep.includes(idx)),
          backgroundColor: Array.isArray(ds.backgroundColor) ? ds.backgroundColor.filter((_, idx) => indicesToKeep.includes(idx)) : ds.backgroundColor,
          borderColor: Array.isArray(ds.borderColor) ? ds.borderColor.filter((_, idx) => indicesToKeep.includes(idx)) : ds.borderColor,
       }))
    };
  }, [dashboardData?.interceptionData, selectedBrands]);

  const chartVisTrend = useMemo(() => {
    const trend = dashboardData?.realVisTrend;
    if (!trend || !trend.labels) return null;
    
    return {
      ...trend,
      datasets: trend.datasets
        .filter(ds => selectedBrands.length === 0 || selectedBrands.includes(ds.label))
        .map(ds => {
        let rgba = ds.backgroundColor;
        if (rgba && rgba.startsWith('#')) {
          const r = parseInt(rgba.slice(1, 3), 16);
          const g = parseInt(rgba.slice(3, 5), 16);
          const b = parseInt(rgba.slice(5, 7), 16);
          rgba = `rgba(${r}, ${g}, ${b}, 0.1)`;
        }
        return {
          ...ds,
          backgroundColor: rgba,
          fill: true
        };
      })
    };
  }, [dashboardData?.realVisTrend, selectedBrands]);

  const chartSovTrend = useMemo(() => {
    const trend = dashboardData?.realSovTrend;
    if (!trend || !trend.labels) return { labels: [], datasets: [] };
    
    return {
      ...trend,
      datasets: trend.datasets
        .filter(ds => selectedBrands.length === 0 || selectedBrands.includes(ds.label))
        .map(ds => {
        let rgba = ds.backgroundColor;
        if (rgba && rgba.startsWith('#')) {
          const r = parseInt(rgba.slice(1, 3), 16);
          const g = parseInt(rgba.slice(3, 5), 16);
          const b = parseInt(rgba.slice(5, 7), 16);
          rgba = `rgba(${r}, ${g}, ${b}, 0.2)`; // Semi-transparent for area fill
        }
        return {
          ...ds,
          fill: true,
          backgroundColor: rgba,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 6,
          tension: 0.4 // Smooth curve
        };
      })
    };
  }, [dashboardData]);

  useEffect(() => {
    if (isMatrixMode && productMappings.length > 0) {
      // In product matrix mode, initialize with the first valid host product and its first valid competitor
      const validMappings = productMappings.filter(p => p.hostProduct?.trim());
      if (validMappings.length > 0) {
        const initialMain = validMappings[0].hostProduct;
        const validComps = validMappings[0].competitorProducts?.filter(cp => cp.prodName?.trim()) || [];
        const initialComp = validComps.length > 0 ? validComps[0].prodName : '';
        
        // If we don't have a selection or the current selection is invalid for the new data
        if (!benchMain || !validMappings.find(p => p.hostProduct === benchMain)) {
          setBenchMain(initialMain);
          setBenchComp(initialComp);
        }
      }
    } else if (competitorTable.length >= 2 && !benchMain && !benchComp) {
      setBenchMain(competitorTable.find(c => c.isSelf)?.brand || competitorTable[0].brand);
      setBenchComp(competitorTable.find(c => !c.isSelf)?.brand || competitorTable[1].brand);
    }
  }, [competitorTable, productMappings, isMatrixMode]);

  
  
  const sovBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y', // horizontal bar
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function(context) {
            return context.label + ': ' + context.parsed.x + '%';
          }
        }
      }
    }
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      line: { tension: 0.4 },
      point: { radius: 0, hitRadius: 10, hoverRadius: 6 }
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      x: { 
        border: { display: true, color: 'rgba(148, 163, 184, 0.3)' },
        grid: { display: false }, 
        ticks: { color: '#94a3b8' } 
      },
      y: { 
        border: { display: true, color: 'rgba(148, 163, 184, 0.3)' },
        grid: { display: false }, 
        ticks: { color: '#64748b' } 
      }
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          boxWidth: 6,
          boxHeight: 6,
          color: '#94a3b8',
          font: { family: 'Inter, sans-serif', size: 12 }
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            return context.dataset.label + ': ' + context.parsed.y + '%';
          }
        }
      }
    }
  };


  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'right', labels: { color: '#94a3b8', font: { family: 'Inter, sans-serif', size: 12 } } },
      tooltip: {
        callbacks: {
          label: function(context) {
            const val = context.parsed;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const pct = total > 0 ? Math.round((val / total) * 100) : 0;
            return context.label + ': ' + val + ' (' + pct + '%)';
          }
        }
      }
    }
  };

    const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (evt, elements) => {
      if (elements.length > 0) {
        const datasetIndex = elements[0].datasetIndex;
        const index = elements[0].index;
        const brand = sentimentData.labels[index];
        const sentimentType = sentimentData.datasets[datasetIndex].label; // e.g. "正面(Positive)"
        if (sentimentType.includes("负向") || sentimentType.includes("负面")) {
           openEvidenceDrawer(`🔍 【${brand}】负向评价溯源明细`, { brand, sentiment: 'negative' });
        } else if (sentimentType.includes("正向") || sentimentType.includes("好评")) {
           openEvidenceDrawer(`🔍 【${brand}】正向评价溯源明细`, { brand, sentiment: 'positive' });
        } else if (sentimentType.includes("争议") || sentimentType.includes("参半") || sentimentType.includes("多面")) {
           openEvidenceDrawer(`🔍 【${brand}】争议评价溯源明细`, { brand, sentiment: 'controversial' });
        } else {
           openEvidenceDrawer(`🔍 【${brand}】中性评价溯源明细`, { brand, sentiment: 'neutral' });
        }
      }
    },
    scales: {
      x: { stacked: true, border: { display: true, color: 'rgba(148, 163, 184, 0.3)' }, grid: { display: false }, ticks: { color: '#94a3b8' } },
      y: { stacked: true, border: { display: true, color: 'rgba(148, 163, 184, 0.3)' }, grid: { display: false }, ticks: { color: '#94a3b8' } }
    },
    plugins: {
      legend: { position: 'top', labels: { color: '#94a3b8' } },
      tooltip: {
        callbacks: {
          footer: () => '👉 点击查看证据溯源'
        }
      }
    }
  };

  
  const defaultPieData = {
    labels: ['暂无数据'],
    datasets: [{
      data: [1],
      backgroundColor: ['rgba(200, 200, 200, 0.2)'],
      borderColor: ['#fff'],
      borderWidth: 1,
    }]
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        angleLines: { color: 'rgba(148, 163, 184, 0.2)' },
        grid: { color: 'rgba(148, 163, 184, 0.2)' },
        pointLabels: { color: '#94a3b8', font: { size: 11 } },
        ticks: { display: false, min: 0, max: 100 }
      }
    },
    plugins: {
      legend: { position: 'bottom', labels: { color: '#94a3b8' } },
      tooltip: {
        callbacks: {
          label: function(context) {
            return context.dataset.label + ': ' + context.parsed.r + '%';
          }
        }
      }
    }
  };

  const getHeatmapColor = (score, maxScore) => {
    if (maxScore === 0) return 'bg-slate-100 dark:bg-slate-700 text-slate-400';
    // Calculate relative percentage against the maximum score present
    const ratio = score / maxScore;
    if (ratio >= 0.9) return 'bg-[#2996cf] text-white';
    if (ratio >= 0.7) return 'bg-[#0ea5e9] text-white';
    if (ratio >= 0.5) return 'bg-sky-400 text-white';
    if (ratio >= 0.3) return 'bg-sky-300 text-slate-800';
    if (ratio > 0) return 'bg-sky-200 text-slate-800';
    return 'bg-slate-100 dark:bg-slate-700 text-slate-400';
  };

  // Find the maximum score in the heatmap matrix
  const maxHeatmapScore = Object.values(heatmapScores || {}).reduce((max, scores) => {
    const rowMax = Math.max(...(scores || [0]));
    return Math.max(max, rowMax);
  }, 0);

  return (
    <ErrorBoundary>
      <div className="space-y-6 max-w-full p-2">
            <div className="flex justify-between items-end">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100 flex items-center space-x-4">
            <span>品牌数据洞察</span>
          </h2>
          <p className="text-gray-500 dark:text-slate-400 mt-2">
            多维度分析品牌在各大生态引擎中的表现与关键词占位情况。
          </p>
        </div>
        <div className="flex items-center gap-3">

          <button 
            className="flex items-center gap-2 px-4 py-2 bg-[#8B84AD] text-white rounded-lg shadow-sm hover:opacity-90 transition-colors text-sm font-medium"
            onClick={handleExportExcel}
          >
            <FileSpreadsheet className="w-4 h-4" />
            导出数据
          </button>
          <button 
            className="flex items-center gap-2 px-4 py-2 bg-[#9EA9DF] text-white rounded-lg shadow-sm hover:opacity-90 transition-colors text-sm font-medium"
            onClick={handleExportImage}
          >
            <Download className="w-4 h-4" />
            导出图片
          </button>
        </div>
      </div>

            {/* Global Filter Bar */}
      <div ref={filterBarRef} className="relative z-40 bg-white dark:bg-slate-800/80 backdrop-blur-xl border border-gray-100 dark:border-slate-700/50 rounded-lg p-3 mb-6 flex flex-wrap items-center gap-3 shadow-sm">
        <div className="flex items-center text-sm font-medium text-gray-700 dark:text-slate-300 mr-2">
          <Filter size={16} className="mr-2 text-blue-500" /> 数据筛选
        </div>
        
        {/* Time Filter */}
        <div className="relative">
          <button 
            onClick={() => toggleDropdown('time')}
            className="flex items-center space-x-2 px-3 py-1.5 bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-600 rounded-md text-sm text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
          >
            <Calendar size={14} />
            <span>{filters.time === '自定义范围' && filters.customDate.start ? `${filters.customDate.start} 至 ${filters.customDate.end}` : filters.time}</span>
            <ChevronDown size={14} className={`text-gray-400 transition-transform ${openDropdown === 'time' ? 'rotate-180' : ''}`} />
          </button>
          {openDropdown === 'time' && (
            <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-md shadow-xl z-50 overflow-hidden">
              {['最近 7 天', '最近 30 天', '最近 90 天', '全部时间', '自定义范围'].map(opt => (
                <div key={opt} onClick={() => updateTimeFilter(opt)} className="px-4 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer flex justify-between items-center">
                  <span>{opt}</span>
                  {filters.time === opt && <Check size={14} className="text-blue-500" />}
                </div>
              ))}
              {filters.time === '自定义范围' && (
                <div className="px-4 py-3 bg-gray-50 dark:bg-slate-900/50 border-t border-gray-100 dark:border-slate-700 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">开始</span>
                    <input type="date" value={filters.customDate.start} onChange={(e) => setFilters(p => ({...p, customDate: {...p.customDate, start: e.target.value}}))} className="text-xs border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">结束</span>
                    <input type="date" value={filters.customDate.end} onChange={(e) => setFilters(p => ({...p, customDate: {...p.customDate, end: e.target.value}}))} className="text-xs border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300" />
                  </div>
                  <button onClick={() => setOpenDropdown(null)} className="w-full mt-2 bg-blue-500 text-white text-xs py-1.5 rounded hover:bg-blue-600 transition">确定</button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* AI Platform Filter (Multi-select) */}
        <div className="relative">
          <button 
            onClick={() => toggleDropdown('engines')}
            className="flex items-center space-x-2 px-3 py-1.5 bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-600 rounded-md text-sm text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
          >
            <span>{renderArrayLabel(filters.engines, '全部搜索平台 (8)')}</span>
            <ChevronDown size={14} className={`text-gray-400 transition-transform ${openDropdown === 'engines' ? 'rotate-180' : ''}`} />
          </button>
          {openDropdown === 'engines' && (
            <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-md shadow-xl z-50 overflow-hidden">
              <div className="py-1">
                <div onClick={() => toggleArrayFilter('engines', '全部搜索平台 (8)', '全部搜索平台 (8)')} className="px-4 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer flex items-center space-x-2 font-medium">
                  <input type="checkbox" checked={filters.engines.includes('全部搜索平台 (8)')} readOnly className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <span>全部搜索平台 (8)</span>
                </div>
                {['百度(PC)', '百度(移动)', '小红书', '知乎', '头条', '抖音', '微博', '微信'].map(opt => {
                  const isDisabled = false;
                  return (
                  <div 
                    key={opt} 
                    onClick={() => { if (!isDisabled) toggleArrayFilter('engines', opt, '全部搜索平台 (8)') }} 
                    className={`px-4 py-2 text-sm flex items-center space-x-2 ${isDisabled ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-slate-900 text-gray-500' : 'text-gray-700 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer'}`}
                    title={isDisabled ? "暂未开放" : ""}
                  >
                    <input type="checkbox" checked={filters.engines.includes(opt)} disabled={isDisabled} readOnly className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed" />
                    <span>{opt}{isDisabled ? ' (暂未开放)' : ''}</span>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        
        {/* Intent Filter */}
        <div className="relative">
          <button onClick={() => toggleDropdown('stages')} className="flex items-center space-x-2 px-3 py-1.5 bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-600 rounded-md text-sm text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
            <span>{renderArrayLabel(filters.stages, '全部阶段', '意图: ')}</span>
            <ChevronDown size={14} className={`text-gray-400 transition-transform ${openDropdown === 'stages' ? 'rotate-180' : ''}`} />
          </button>
          {openDropdown === 'stages' && (
            <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-md shadow-xl z-50 overflow-hidden">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50 dark:bg-slate-900/50 border-b border-gray-100 dark:border-slate-700 flex justify-between">
                <span>SICAS 漏斗 (可多选)</span>
              </div>
              <div onClick={() => toggleArrayFilter('stages', '全部阶段', '全部阶段')} className="px-4 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer flex items-center space-x-2 font-medium">
                <input type="checkbox" checked={filters.stages.includes('全部阶段')} readOnly className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span>全部阶段</span>
              </div>
              <div onClick={() => toggleArrayFilter('stages', 'Sense', '全部阶段')} className="px-4 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer flex items-start space-x-2">
                <input type="checkbox" checked={filters.stages.includes('Sense')} readOnly className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1" />
                <div>
                  <div className="font-medium">Sense (感知)</div>
                  <div className="text-[10px] text-gray-400">是什么、原理、科普</div>
                </div>
              </div>
              <div onClick={() => toggleArrayFilter('stages', 'Interest', '全部阶段')} className="px-4 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer flex items-start space-x-2">
                 <input type="checkbox" checked={filters.stages.includes('Interest')} readOnly className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1" />
                <div>
                  <div className="font-medium">Interest (兴趣)</div>
                  <div className="text-[10px] text-gray-400">评测、好处、种草</div>
                </div>
              </div>
              <div onClick={() => toggleArrayFilter('stages', 'Connection', '全部阶段')} className="px-4 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer bg-blue-50/30 flex items-start space-x-2">
                <input type="checkbox" checked={filters.stages.includes('Connection')} readOnly className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1" />
                <div>
                  <div className="font-medium flex items-center text-blue-600 dark:text-blue-400">
                    Connection (连接) 
                    <span className="ml-2 text-[9px] bg-red-100 text-red-600 px-1 py-0.5 rounded border border-red-200">防守核心</span>
                  </div>
                  <div className="text-[10px] text-gray-500">对比、哪个好、价格</div>
                </div>
              </div>
              <div onClick={() => toggleArrayFilter('stages', 'Action', '全部阶段')} className="px-4 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer flex items-start space-x-2">
                <input type="checkbox" checked={filters.stages.includes('Action')} readOnly className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1" />
                <div>
                  <div className="font-medium">Action (行动)</div>
                  <div className="text-[10px] text-gray-400">购买、官网、下载</div>
                </div>
              </div>
              <div onClick={() => toggleArrayFilter('stages', 'Share', '全部阶段')} className="px-4 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer flex items-start space-x-2">
                <input type="checkbox" checked={filters.stages.includes('Share')} readOnly className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1" />
                <div>
                  <div className="font-medium">Share (分享)</div>
                  <div className="text-[10px] text-gray-400">维权、售后、吐槽</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Personas Filter */}
        <div className="relative">
          <button onClick={() => toggleDropdown('keywordTypes')} className="flex items-center space-x-2 px-3 py-1.5 bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-600 rounded-md text-sm text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
            <span>{renderArrayLabel(filters.keywordTypes, '全部词汇属性', '词类: ')}</span>
            <ChevronDown size={14} className={`text-gray-400 transition-transform ${openDropdown === 'keywordTypes' ? 'rotate-180' : ''}`} />
          </button>
          {openDropdown === 'keywordTypes' && (
            <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-md shadow-xl z-50 overflow-hidden">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50 dark:bg-slate-900/50 border-b border-gray-100 dark:border-slate-700">可多选</div>
              <div className="py-1">
                {['全部词汇属性', '品牌词', '竞品词', '对比词', '通用词'].map(opt => (
                  <div key={opt} onClick={() => toggleArrayFilter('keywordTypes', opt, '全部词汇属性')} className="px-4 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer flex items-center space-x-2">
                    <input type="checkbox" checked={filters.keywordTypes.includes(opt)} readOnly className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    <span>{opt}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div id="report-content" className="space-y-6 pt-2 pb-4 px-1 bg-transparent">
      {!dashboardData && !loading ? (
        <div className="flex flex-col items-center justify-center h-64 text-slate-500 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700">
          <svg className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          请先在顶部选择品牌与项目
        </div>
      ) : (
        <>
          <div className={`transition-all duration-300 ${loading ? 'opacity-50 pointer-events-none blur-[2px]' : ''}`}>
          {activeView === 'global' ? (
        <>
          {/* Brand Filter for Charts */}
          <div className="bg-white dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200 dark:border-slate-700/50 rounded-xl p-4 shadow-sm mb-6 flex items-center justify-between flex-wrap gap-4">
             <div className="flex items-center gap-3 flex-wrap">
                <div className="flex flex-col gap-3 w-full">
               {/* Line 1: Pre-configured competitors */}
               <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-gray-700 dark:text-slate-300 whitespace-nowrap mt-1">核心设定竞品:</span>
                  <div className="flex flex-wrap gap-2">
                     {[...new Set(heatmapBrands)].filter(b => !(dashboardData?.aiDiscoveredBrands || []).includes(b)).map(b => {
                        if (!b ) return null;
                        const isSelected = selectedBrands.includes(b);
                        return (
                           <label key={b} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors border ${isSelected ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-800/50 dark:text-blue-400' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-700'}`}>
                              <input 
                                 type="checkbox" 
                                 className="sr-only" 
                                 checked={isSelected}
                                 onChange={() => {
                                    if (isSelected && selectedBrands.length > 1) {
                                       setSelectedBrands(selectedBrands.filter(sb => sb !== b));
                                    } else if (!isSelected && selectedBrands.length < 8) {
                                       setSelectedBrands([...selectedBrands, b]);
                                    } else if (!isSelected && selectedBrands.length >= 8) {
                                       alert('为了保证图表可读性，最多只能同时选中 8 个品牌进行对比哦。');
                                    }
                                 }}
                              />
                              {isSelected && <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
                              {b}
                           </label>
                        )
                     })}
                  </div>
               </div>

               {/* Line 2: AI Discovered Brands */}
               {dashboardData?.aiDiscoveredBrands !== undefined && (
                  <div className="flex items-start gap-3 border-t border-gray-100 dark:border-slate-700/50 pt-3">
                     <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400 whitespace-nowrap mt-1 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143z" /></svg>
                        AI 挖掘竞品:
                     </span>
                     <div className="flex flex-wrap gap-2">
                     {dashboardData?.aiDiscoveredBrands?.length === 0 && (
                        <span className="text-xs text-gray-400 dark:text-slate-500 mt-1">本周期内暂无新发现的竞品</span>
                     )}
                     {dashboardData?.aiDiscoveredBrands?.map(b => {
                           if (!b) return null;
                           const isSelected = selectedBrands.includes(b);
                           return (
                              <label key={b} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors border ${isSelected ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-800/50 dark:text-emerald-400' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-700'}`}>
                                 <input 
                                    type="checkbox" 
                                    className="sr-only" 
                                    checked={isSelected}
                                    onChange={() => {
                                       if (isSelected && selectedBrands.length > 1) {
                                          setSelectedBrands(selectedBrands.filter(sb => sb !== b));
                                       } else if (!isSelected && selectedBrands.length < 8) {
                                          setSelectedBrands([...selectedBrands, b]);
                                       } else if (!isSelected && selectedBrands.length >= 8) {
                                          alert('为了保证图表可读性，最多只能同时选中 8 个品牌进行对比哦。');
                                       }
                                    }}
                                 />
                                 {isSelected && <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
                                 {b}
                              </label>
                           )
                     })}
                     </div>
                  </div>
               )}
               </div>
             </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200 dark:border-slate-700/50 rounded-xl p-6 shadow-sm">
              <h3 className="text-base font-bold text-gray-900 dark:text-slate-200 mb-4">SEO 综合竞争力六维雷达 (SEO Competitiveness Radar)</h3>
              <div className="h-64"><Radar data={chartRadarData} options={radarOptions} /></div>
            </div>
            <div className="bg-white dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200 dark:border-slate-700/50 rounded-xl p-6 shadow-sm">
              <h3 className="text-base font-bold text-gray-900 dark:text-slate-200 mb-4">各平台搜索结果情感对比 (Sentiment Comparison)</h3>
              <div className="h-64"><Bar data={chartSentimentData} options={barOptions} /></div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200 dark:border-slate-700/50 rounded-xl p-6 shadow-sm mb-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-base font-bold text-gray-900 dark:text-slate-200">SOV 提及率分析 (Share of Voice)</h3>
              <div className="hidden flex bg-slate-100 dark:bg-slate-700/50 rounded-lg p-1">
                <button className="px-3 py-1.5 text-xs font-medium rounded-md bg-white dark:bg-slate-600 text-gray-800 dark:text-white shadow-sm transition-all">全网综合</button>
                <button className="px-3 py-1.5 text-xs font-medium rounded-md text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-all">百度</button>
                <button className="px-3 py-1.5 text-xs font-medium rounded-md text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-all">小红书</button>
                <button className="px-3 py-1.5 text-xs font-medium rounded-md text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-all">抖音</button>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1 border-r-0 lg:border-r border-gray-100 dark:border-slate-700/50 pr-0 lg:pr-6">
                <h4 className="text-sm font-semibold text-gray-600 dark:text-slate-400 mb-4">各品牌 SOV 占比</h4>
                <div className="h-64"><Bar data={sovData} options={{...sovBarOptions, plugins: { ...sovBarOptions.plugins, legend: { display: false } }, scales: { x: { stacked: true, border: { display: true, color: 'rgba(148, 163, 184, 0.3)' }, grid: { display: false }, ticks: { color: '#94a3b8' } }, y: { stacked: true, border: { display: true, color: 'rgba(148, 163, 184, 0.3)' }, grid: { display: false }, ticks: { color: '#94a3b8' } } }}} /></div>
              </div>
              <div className="lg:col-span-2">
                <h4 className="text-sm font-semibold text-gray-600 dark:text-slate-400 mb-4">SOV 份额攻防走势</h4>
                <div className="h-64">
                  {chartSovTrend?.labels?.length > 0 ? (
                    <Line data={chartSovTrend} options={lineOptions} />
                  ) : (
                    <div className="flex h-full items-center justify-center text-slate-400 text-sm">
                      暂无足够的历史趋势数据
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200 dark:border-slate-700/50 rounded-xl p-6 shadow-sm mb-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-base font-bold text-gray-900 dark:text-slate-200">品牌能见度分析 (Brand Visibility)</h3>
              <div className="hidden flex bg-slate-100 dark:bg-slate-700/50 rounded-lg p-1">
                <button className="px-3 py-1.5 text-xs font-medium rounded-md bg-white dark:bg-slate-600 text-gray-800 dark:text-white shadow-sm transition-all">全网综合</button>
                <button className="px-3 py-1.5 text-xs font-medium rounded-md text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-all">百度</button>
                <button className="px-3 py-1.5 text-xs font-medium rounded-md text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-all">小红书</button>
                <button className="px-3 py-1.5 text-xs font-medium rounded-md text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-all">抖音</button>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1 border-r-0 lg:border-r border-gray-100 dark:border-slate-700/50 pr-0 lg:pr-6">
                <h4 className="text-sm font-semibold text-gray-600 dark:text-slate-400 mb-4">各品牌能见度对比</h4>
                <div className="h-64"><Bar data={visData || sovData} options={{...sovBarOptions, plugins: { ...sovBarOptions.plugins, legend: { display: false } }, scales: { x: { stacked: true, border: { display: true, color: 'rgba(148, 163, 184, 0.3)' }, grid: { display: false }, ticks: { color: '#94a3b8' } }, y: { stacked: true, border: { display: true, color: 'rgba(148, 163, 184, 0.3)' }, grid: { display: false }, ticks: { color: '#94a3b8' } } }}} /></div>
              </div>
              <div className="lg:col-span-2">
                <h4 className="text-sm font-semibold text-gray-600 dark:text-slate-400 mb-4">能见度攻防走势</h4>
                <div className="h-64">
                  {chartSovTrend?.labels?.length > 0 ? (
                    <Line data={chartVisTrend || chartSovTrend} options={lineOptions} />
                  ) : (
                    <div className="flex h-full items-center justify-center text-slate-400 text-sm">
                      暂无足够的历史趋势数据
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* RADAR MOVED UP */}
          
          <div className="mt-6 border border-gray-200 dark:border-slate-700/50 rounded-xl bg-white dark:bg-slate-800/50 backdrop-blur-xl shadow-sm overflow-hidden">
            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-base font-bold text-gray-900 dark:text-slate-200 flex items-center gap-2">
                    <span className="w-1.5 h-4 bg-orange-500 rounded-full"></span>
                    核心词包攻防战报 (Interception & Defense)
                  </h3>
                </div>
                <div className="h-64">
                  <Bar 
                    data={interceptionData?.labels?.length > 0 ? interceptionData : {
                      labels: ['本品', '竞品A', '竞品B'],
                      datasets: [
                        {
                          label: '品牌词占位率 (防守)',
                          data: [85, 20, 15],
                          backgroundColor: 'rgba(41, 150, 207, 0.8)',
                          borderRadius: 4
                        },
                        {
                          label: '竞品词拦截率 (进攻)',
                          data: [35, 60, 45],
                          backgroundColor: 'rgba(234, 188, 107, 0.8)',
                          borderRadius: 4
                        }
                      ]
                    }} 
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          max: 100,
                          ticks: { color: '#94a3b8', callback: function(value) { return value + '%'; } },
                          border: { display: true, color: 'rgba(148, 163, 184, 0.3)' },
                          grid: { display: false }
                        },
                        x: {
                          ticks: { color: '#94a3b8' },
                          border: { display: true, color: 'rgba(148, 163, 184, 0.3)' },
                          grid: { display: false }
                        }
                      },
                      plugins: {
                        legend: { position: 'bottom', labels: { color: '#94a3b8' } },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              return context.dataset.label + ': ' + context.parsed.y + '%';
                            }
                          }
                        }
                      }
                    }} 
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-base font-bold text-gray-900 dark:text-slate-200 flex items-center gap-2">
                    <span className="w-1.5 h-4 bg-blue-500 rounded-full"></span>
                    媒体生态与自有阵地收录 (Owned Media)
                  </h3>
                  <button 
                    onClick={() => openMediaEvidenceDrawer('📊 媒体与内容溯源明细')}
                    className="text-xs flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 bg-blue-50 dark:bg-blue-900/30 px-3 py-1.5 rounded-md transition-colors"
                  >
                    <Search className="w-3.5 h-3.5" />
                    查看明细 & 导出
                  </button>
                </div>
                <div className="h-64">
                  <Doughnut 
                    data={dashboardData?.mediaEcosystemData || {
                      labels: ['自有阵地 (官网/官微/小程序)', '权威媒体 (新闻源)', '垂直社区 (知乎/小红书)', '其他 UGC'],
                      datasets: [{
                        data: [35, 25, 30, 10],
                        backgroundColor: [
                          'rgba(41, 150, 207, 0.9)', // 自有阵地 (主色高亮)
                          'rgba(148, 163, 184, 0.8)', 
                          'rgba(234, 188, 107, 0.8)',
                          'rgba(203, 213, 225, 0.6)'
                        ],
                        borderWidth: 0
                      }]
                    }} 
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { position: 'right', labels: { color: '#94a3b8' } }
                      },
                      cutout: '65%'
                    }} 
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6 bg-white dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200 dark:border-slate-700/50 rounded-xl p-6 shadow-sm overflow-x-auto">
            <div className="flex justify-between items-center mb-6">
              <div className="flex flex-col">
                <h3 className="text-base font-bold text-gray-900 dark:text-slate-200 flex items-center gap-2">
                  各业务维度表现热力图 (Business Dimension Matrix)
                </h3>
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                  数值代表该品牌在各业务维度下，被引擎输出为<span className="text-emerald-500 font-medium px-1">正向推荐（好评）</span>的百分比表现。颜色越深，口碑与好感度越佳。
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="hidden flex bg-slate-100 dark:bg-slate-700/50 rounded-lg p-1">
                  <button className="px-3 py-1 text-xs font-medium rounded-md bg-white dark:bg-slate-600 text-gray-800 dark:text-white shadow-sm transition-all">SOV 提及率</button>
                  <button className="px-3 py-1 text-xs font-medium rounded-md text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-all">能见度</button>
                </div>
                <div className="hidden flex bg-slate-100 dark:bg-slate-700/50 rounded-lg p-1">
                  <button className="px-3 py-1 text-xs font-medium rounded-md bg-white dark:bg-slate-600 text-gray-800 dark:text-white shadow-sm transition-all">全网综合</button>
                  <button className="px-3 py-1 text-xs font-medium rounded-md text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-all">百度</button>
                  <button className="px-3 py-1 text-xs font-medium rounded-md text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-all">小红书</button>
                  <button className="px-3 py-1 text-xs font-medium rounded-md text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-all">抖音</button>
                </div>
              </div>
            </div>
            <div className="min-w-[800px]">
              <div className="grid gap-1 mb-2" style={{ gridTemplateColumns: `140px repeat(${Math.max(1, heatmapDimensions.length)}, minmax(0, 1fr))` }}>
                <div className="text-xs font-medium text-slate-400 flex items-end pb-2">品牌 \\ 维度</div>
                {heatmapDimensions.map(dim => (
                  <div key={dim} className="text-[11px] font-medium text-slate-500 dark:text-slate-400 text-center pb-2 break-words leading-tight flex items-end justify-center">
                    <span className="px-2 text-center break-words">{dim}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-1">
                {[...new Set(displayBrands.filter(b => b && typeof b === 'string' && b.trim() !== ''))].map(brand => (
                  <div key={brand} className="grid gap-1" style={{ gridTemplateColumns: `140px repeat(${Math.max(1, heatmapDimensions.length)}, minmax(0, 1fr))` }}>
                    <div className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center">{brand || '未命名'}</div>
                    {heatmapScores[brand]?.map((score, idx) => (
                      <div key={idx} className={`h-10 rounded flex items-center justify-center text-xs font-medium transition-colors ${getHeatmapColor(score, maxHeatmapScore)}`} title={`${brand} - ${heatmapDimensions[idx]}: ${score}%`}>
                        {score}%
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 bg-white dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200 dark:border-slate-700/50 rounded-xl p-6 shadow-sm overflow-x-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-base font-bold text-gray-900 dark:text-slate-200">关键词攻防机会矩阵 (Keyword Opportunity Matrix)</h3>
              <div className="flex bg-slate-100 dark:bg-slate-700/50 rounded-lg p-1">
                {['品牌词', '竞品词', '对比词', '通用词'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveBubbleTab(tab)}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                      activeBubbleTab === tab
                        ? 'bg-white dark:bg-slate-600 text-gray-800 dark:text-white shadow-sm'
                        : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-[450px] relative">
              <Bubble 
                data={{
                  datasets: (selectedBrands.length > 0 ? selectedBrands : brandsList).map((brand, i) => ({
                    label: brand,
                    data: dashboardData?.keywordMatrixData?.[activeBubbleTab]?.[brand] || [],
                    backgroundColor: getBubbleBgColor(colorsList[i % colorsList.length]),
                    borderColor: getBorderColor(colorsList[i % colorsList.length]),
                    borderWidth: 1
                  }))
                }} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  interaction: {
                    mode: 'nearest',
                    axis: 'xy',
                    intersect: false,
                  },
                  scales: {
                    x: {
                      border: { display: true, color: 'rgba(148, 163, 184, 0.3)' },
                      title: { display: true, text: '提及率 (%)', color: '#94a3b8' },
                      grid: { display: false },
                      min: 0,
                      grace: '10%',
                      ticks: { color: '#94a3b8', callback: function(value) { return value + '%'; } }
                    },
                    y: {
                      border: { display: true, color: 'rgba(148, 163, 184, 0.3)' },
                      title: { display: true, text: '能见度 (%)', color: '#94a3b8' },
                      grid: { display: false },
                      min: 0,
                      grace: '15%',
                      ticks: { color: '#94a3b8', callback: function(value) { return value + '%'; } }
                    }
                  },
                  plugins: {
                    legend: { position: 'bottom', labels: { color: '#94a3b8' } },
                    tooltip: {
                      callbacks: {
                        label: function(ctx) {
                          const vol = ctx.raw.searchVolume || Math.round(ctx.raw.r * 150); 
                          return `${ctx.raw.keyword}: 提及率 ${ctx.raw.x.toFixed(1)}%, 能见度 ${ctx.raw.y.toFixed(1)}%, 搜索量 ${vol}`;
                        }
                      }
                    }
                  }
                }}
              />
              <div className="absolute inset-0 pointer-events-none flex">
                 {/* This div just draws the quadrant crosshairs visually behind the chart if needed, but Chart.js grid might be enough. We'll stick to Chart.js native rendering for now */}
              </div>
            </div>
          </div>





          
          {/* AI Cognitive Word Cloud */}
          {dashboardData && Object.keys(wordCloudData).length > 0 && heatmapBrands.length > 0 && (
            <div className="mt-6">
              <WordCloudCard 
                data={wordCloudData} 
                brands={heatmapBrands} 
                onWordClick={(theme, sentiment, brand) => {
                  let sentimentLabel = sentiment === 'positive' ? '正向评价' : sentiment === 'negative' ? '负向评价' : '中性客观';
                  openEvidenceDrawer(`🔍 【${brand}】${theme} (${sentimentLabel}) 溯源明细`, { brand, theme, sentiment });
                }} 
              />
            </div>
          )}

        </>
      ) : (
        <div className="space-y-6">
          {/* Benchmarking View Implementation */}
          <div className="bg-white dark:bg-slate-800/80 backdrop-blur-xl border border-gray-100 dark:border-slate-700/50 rounded-xl p-6 shadow-sm mb-6">
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="flex-1 flex items-center space-x-3 w-full">
                <div className="text-sm font-semibold text-gray-500 dark:text-slate-400 w-16 shrink-0">{dashboardData?.isMatrixMode ? '主推产品' : '主推品牌'}</div>
                <select 
                  className="flex-1 bg-slate-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-800 dark:text-slate-200 focus:outline-none focus:border-[#2996cf]"
                  value={benchMain}
                  onChange={e => {
                    const newMain = e.target.value;
                    setBenchMain(newMain);
                    if (isMatrixMode) {
                      const mapping = productMappings.find(p => p.hostProduct === newMain);
                      const validComps = mapping?.competitorProducts?.filter(cp => cp.prodName?.trim()) || [];
                      if (validComps.length > 0) {
                        setBenchComp(validComps[0].prodName);
                      }
                    }
                  }}
                >
                  {isMatrixMode 
                    ? productMappings.filter(p => p.hostProduct?.trim()).map(p => <option key={p.hostProduct} value={p.hostProduct}>{p.hostProduct}</option>)
                    : competitorTable.map(c => <option key={c.brand} value={c.brand}>{c.brand} {c.isSelf ? '(本品)' : ''}</option>)
                  }
                </select>
              </div>
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-rose-50 dark:bg-rose-900/20 text-rose-500 font-bold shrink-0 text-xs">VS</div>
              <div className="flex-1 flex items-center space-x-3 w-full">
                <div className="text-sm font-semibold text-gray-500 dark:text-slate-400 w-16 shrink-0">{dashboardData?.isMatrixMode ? '对标产品' : '对标竞品'}</div>
                <select 
                  className="flex-1 bg-slate-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-800 dark:text-slate-200 focus:outline-none focus:border-rose-400"
                  value={benchComp}
                  onChange={e => setBenchComp(e.target.value)}
                >
                  {isMatrixMode
                    ? (productMappings.find(p => p.hostProduct === benchMain)?.competitorProducts || []).filter(cp => cp.prodName?.trim()).map(cp => <option key={cp.prodName} value={cp.prodName}>{cp.prodName}</option>)
                    : competitorTable.map(c => <option key={c.brand} value={c.brand}>{c.brand}</option>)
                  }
                </select>
              </div>
              <div className="w-px h-10 bg-gray-200 dark:bg-slate-700 mx-2 hidden md:block"></div>
              <div className="flex-1 flex items-center space-x-3 w-full">
                <div className="text-sm font-semibold text-gray-500 dark:text-slate-400 w-16 shrink-0">洞察人设</div>
                <select 
                  className="flex-1 bg-slate-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-800 dark:text-slate-200 focus:outline-none focus:border-emerald-400"
                  value={benchPersona}
                  onChange={e => setBenchPersona(e.target.value)}
                >
                  <option>所有人群 (全局视图)</option>
                  {dashboardData?.availablePersonas?.map((p, i) => (
                     <option key={i} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {benchMain && benchComp && benchMain !== benchComp ? (
            <div className="space-y-6">
              {/* Core Stats Duel */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200 dark:border-slate-700/50 rounded-xl p-6 shadow-sm">
                  <h3 className="text-base font-bold text-gray-900 dark:text-slate-200 mb-6 flex items-center">
                    <Activity className="w-5 h-5 mr-2 text-[#2996cf]" /> 核心数据对决 (1v1)
                  </h3>
                  
                  <div className="space-y-6">
                    {/* SOV Duel */}
                    {(() => {
                      const mainSovRaw = competitorTable.find(c => c.brand === benchMain)?.sov || '0%';
                      const compSovRaw = competitorTable.find(c => c.brand === benchComp)?.sov || '0%';
                      const mainSov = parseFloat(mainSovRaw) || 0;
                      const compSov = parseFloat(compSovRaw) || 0;
                      const bothZero = mainSov === 0 && compSov === 0;
                      let mainPct = 0, compPct = 0;
                      if (!bothZero) {
                        const totalSov = mainSov + compSov;
                        mainPct = Math.round((mainSov / totalSov) * 100);
                        compPct = 100 - mainPct;
                      }
                      return (
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="font-semibold text-[#2996cf]">{benchMain}</span>
                            <span className="text-gray-500 text-xs">相对 SOV 提及率</span>
                            <span className="font-semibold text-rose-500">{benchComp}</span>
                          </div>
                          <div className="flex h-3 rounded-full overflow-hidden bg-gray-100 dark:bg-slate-700">
                            {bothZero ? (
                              <div className="bg-purple-300 dark:bg-slate-600 transition-all duration-500 w-full flex items-center justify-center"></div>
                            ) : (
                              <>
                                <div className="bg-[#2996cf] transition-all duration-500" style={{ width: `${mainPct}%` }}></div>
                                <div className="bg-rose-400 transition-all duration-500" style={{ width: `${compPct}%` }}></div>
                              </>
                            )}
                          </div>
                          <div className="flex justify-between text-xs mt-1 text-slate-500">
                            <span>{mainSovRaw}</span>
                            <span>{compSovRaw}</span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Sentiment Duel */}
                    {(() => {
                      const mainRecRaw = competitorTable.find(c => c.brand === benchMain)?.recommendRate || '0%';
                      const compRecRaw = competitorTable.find(c => c.brand === benchComp)?.recommendRate || '0%';
                      const mainRec = parseFloat(mainRecRaw) || 0;
                      const compRec = parseFloat(compRecRaw) || 0;
                      const bothZero = mainRec === 0 && compRec === 0;
                      let mainPct = 0, compPct = 0;
                      if (!bothZero) {
                        const totalRec = mainRec + compRec;
                        mainPct = Math.round((mainRec / totalRec) * 100);
                        compPct = 100 - mainPct;
                      }
                      return (
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="font-semibold text-[#2996cf]">{benchMain}</span>
                            <span className="text-gray-500 text-xs">综合好感度对抗</span>
                            <span className="font-semibold text-rose-500">{benchComp}</span>
                          </div>
                          <div className="flex h-3 rounded-full overflow-hidden bg-gray-100 dark:bg-slate-700">
                            {bothZero ? (
                              <div className="bg-purple-300 dark:bg-slate-600 transition-all duration-500 w-full flex items-center justify-center"></div>
                            ) : (
                              <>
                                <div className="bg-[#2996cf] transition-all duration-500" style={{ width: `${mainPct}%` }}></div>
                                <div className="bg-rose-400 transition-all duration-500" style={{ width: `${compPct}%` }}></div>
                              </>
                            )}
                          </div>
                          <div className="flex justify-between text-xs mt-1 text-slate-500">
                            <span>{mainRecRaw}</span>
                            <span>{compRecRaw}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Radar Duel */}
                <div className="bg-white dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200 dark:border-slate-700/50 rounded-xl p-6 shadow-sm">
                  <h3 className="text-base font-bold text-gray-900 dark:text-slate-200 mb-2 flex items-center">
                    <Target className="w-5 h-5 mr-2 text-[#2996cf]" /> 能力维度重叠分析
                  </h3>
                  <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
                     <Radar data={{
                        labels: heatmapDimensions.length > 0 ? heatmapDimensions : ['性能', '价格', '外观', '智能', '续航'],
                        datasets: [
                          {
                            label: benchMain,
                            data: heatmapScores[benchMain] || [0,0,0,0,0],
                            backgroundColor: 'rgba(41, 150, 207, 0.2)',
                            borderColor: '#2996cf',
                            borderWidth: 2,
                          },
                          {
                            label: benchComp,
                            data: heatmapScores[benchComp] || [0,0,0,0,0],
                            backgroundColor: 'rgba(244, 63, 94, 0.2)',
                            borderColor: '#f43f5e',
                            borderWidth: 2,
                          }
                        ]
                     }} options={radarOptions} />
                  </div>
                </div>
              </div>

              {/* Pros vs Cons Duel */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-sky-50/50 dark:bg-slate-800/50 border border-sky-100 dark:border-slate-700/50 rounded-xl p-6 shadow-sm">
                  <h4 className="text-base font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#2996cf]"></div>
                    {benchMain} 核心卖点与痛点
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-emerald-600 dark:text-emerald-400 font-medium mb-2 flex items-center"><Check className="w-4 h-4 mr-1"/> 引擎共识优势 (Pros)</div>
                      <div className="flex flex-wrap gap-2">
                        {competitorTable.find(c => c.brand === benchMain)?.pros?.map((p, i) => (
                           <span key={i} className="px-3 py-1.5 bg-white dark:bg-slate-800 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 rounded-lg text-sm shadow-sm">{p}</span>
                        )) || <span className="text-slate-400 text-sm">暂无数据</span>}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-rose-600 dark:text-rose-400 font-medium mb-2 flex items-center"><X className="w-4 h-4 mr-1"/> 引擎共识痛点 (Cons)</div>
                      <div className="flex flex-wrap gap-2">
                        {competitorTable.find(c => c.brand === benchMain)?.cons?.map((c, i) => (
                           <span key={i} className="px-3 py-1.5 bg-white dark:bg-slate-800 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-400 rounded-lg text-sm shadow-sm cursor-pointer hover:bg-rose-50 dark:hover:bg-rose-900/40" onClick={() => openEvidenceDrawer(`🔍 【${benchMain}】核心劣势 (${c}) 原始对话`, { brand: benchMain, cons: c })}>{c}</span>
                        )) || <span className="text-slate-400 text-sm">暂无数据</span>}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-rose-50/30 dark:bg-slate-800/50 border border-rose-100 dark:border-slate-700/50 rounded-xl p-6 shadow-sm">
                  <h4 className="text-base font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-rose-400"></div>
                    {benchComp} 核心卖点与痛点
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-emerald-600 dark:text-emerald-400 font-medium mb-2 flex items-center"><Check className="w-4 h-4 mr-1"/> 引擎共识优势 (Pros)</div>
                      <div className="flex flex-wrap gap-2">
                        {competitorTable.find(c => c.brand === benchComp)?.pros?.map((p, i) => (
                           <span key={i} className="px-3 py-1.5 bg-white dark:bg-slate-800 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 rounded-lg text-sm shadow-sm">{p}</span>
                        )) || <span className="text-slate-400 text-sm">暂无数据</span>}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-rose-600 dark:text-rose-400 font-medium mb-2 flex items-center"><X className="w-4 h-4 mr-1"/> 引擎共识痛点 (Cons)</div>
                      <div className="flex flex-wrap gap-2">
                        {competitorTable.find(c => c.brand === benchComp)?.cons?.map((c, i) => (
                           <span key={i} className="px-3 py-1.5 bg-white dark:bg-slate-800 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-400 rounded-lg text-sm shadow-sm cursor-pointer hover:bg-rose-50 dark:hover:bg-rose-900/40" onClick={() => openEvidenceDrawer(`🔍 【${benchComp}】核心劣势 (${c}) 原始对话`, { brand: benchComp, cons: c })}>{c}</span>
                        )) || <span className="text-slate-400 text-sm">暂无数据</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dynamic Win-Rate Matrix */}
              <div className="bg-white dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200 dark:border-slate-700/50 rounded-xl p-6 shadow-sm">
                <h3 className="text-base font-bold text-gray-900 dark:text-slate-200 mb-4 flex items-center">
                  <Activity className="w-5 h-5 mr-2 text-[#2996cf]" /> {benchPersona} 下的场景胜率地图
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-slate-700 text-sm text-slate-500">
                        <th className="pb-3 font-medium">应用场景 / 评估维度</th>
                        <th className="pb-3 font-medium text-center">{benchMain} 胜率</th>
                        <th className="pb-3 font-medium text-center">{benchComp} 胜率</th>
                        <th className="pb-3 font-medium">引擎倾向性结论</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm">
                      {heatmapDimensions.length > 0 ? heatmapDimensions.map((dim, idx) => {
                        const mainScore = heatmapScores[benchMain]?.[idx] || 0;
                        const compScore = heatmapScores[benchComp]?.[idx] || 0;
                        let conclusion = '';
                        if (mainScore > compScore + 10) conclusion = `在"${dim}"相关提问中，AI明显偏向推荐 ${benchMain}。`;
                        else if (compScore > mainScore + 10) conclusion = `在"${dim}"相关提问中，AI明显偏向推荐 ${benchComp}。`;
                        else conclusion = `在"${dim}"相关提问中，AI对两者的评价势均力敌。`;
                        
                        return (
                          <tr key={idx} className="border-b border-gray-100 dark:border-slate-700/50 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                            <td className="py-4 font-medium text-gray-800 dark:text-slate-300 pl-2">{dim}</td>
                            <td className="py-4 text-center text-[#2996cf] font-bold">{mainScore}%</td>
                            <td className="py-4 text-center text-rose-500 font-bold">{compScore}%</td>
                            <td className="py-4 text-gray-600 dark:text-slate-400 text-xs">
                               {conclusion}
                            </td>
                          </tr>
                        );
                      }) : (
                        <tr><td colSpan="4" className="py-8 text-center text-slate-500">暂无维度数据</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
             <div className="flex flex-col items-center justify-center py-16 text-center bg-white dark:bg-slate-800/50 rounded-xl border border-gray-100 dark:border-slate-700/50">
                <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4 text-slate-400">
                  <Target className="w-8 h-8" />
                </div>
                <h3 className="text-base font-medium text-gray-900 dark:text-slate-200">请选择不同的主推品牌与竞品进行对标</h3>
             </div>
          )}
        </div>
      )}
      </div>
      </>
      )}
      </div>

      {/* Evidence Drawer Overlay & Content */}
      <div className={`fixed inset-0 z-[60] transition-opacity duration-300 ${isDrawerOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        {/* Backdrop */}
        <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={closeDrawer}></div>
        
        {/* Drawer Panel */}
        <div className={`absolute top-0 right-0 h-full w-full sm:w-[600px] bg-white dark:bg-slate-900 border-l border-gray-200 dark:border-slate-800 shadow-2xl transition-transform duration-300 transform flex flex-col ${isDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-800 flex items-center justify-between bg-gray-50/50 dark:bg-slate-900/50">
            <h3 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#2996cf]" />
              {drawerTitle}
            </h3>
            <button onClick={closeDrawer} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar bg-slate-50/30 dark:bg-slate-900">
            {drawerLoading ? (
               <div className="flex justify-center items-center h-full text-slate-400">正在检索溯源记录...</div>
            ) : (
               drawerData.map((record, index) => (
                 <div key={index} className="bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden mb-6">
                    <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700/50 bg-gray-50/50 dark:bg-slate-800/50 flex justify-between items-center">
                       <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                          {engineLogos[record.platform] && <img src={engineLogos[record.platform]} className="w-5 h-5" />}
                          {record.platform}
                       </div>
                       <div className="text-xs text-slate-400">{record.time}</div>
                    </div>
                    <div className="p-4 space-y-4">
                        {/* User Question */}
                        <div className="flex justify-end pr-2">
                          <div className="bg-[#2996cf] text-white rounded-2xl rounded-tr-sm px-4 py-3 max-w-[90%] shadow-sm text-sm">
                            {record.question}
                          </div>
                        
                            
                        </div>
                        {/* AI Response */}
                        <div className="flex justify-start pl-2">
                          <div className="bg-gray-50 dark:bg-slate-900 text-slate-700 dark:text-slate-300 rounded-2xl rounded-tl-sm px-4 py-4 max-w-[95%] border border-gray-200 dark:border-slate-700 shadow-sm text-sm prose prose-sm dark:prose-invert max-w-none max-h-[400px] overflow-y-auto custom-scrollbar whitespace-pre-wrap">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{record.rawResponse}</ReactMarkdown>
                            
                            {/* 小红书信源瀑布流展示 */}
                            {record.xhsRawNotes && record.xhsRawNotes.length > 0 ? (
                              <div className="mt-4 pt-3 border-t border-gray-200 dark:border-slate-700">
                                <div className="flex items-center justify-between mb-3">
                                  <p className="text-xs font-bold text-gray-500 dark:text-slate-400">引用溯源 (小红书笔记):</p>
                                </div>
                                <div className="columns-1 sm:columns-2 gap-4 space-y-4">
                                  {record.xhsRawNotes.map((note, idx) => (
                                    <div key={idx} className="break-inside-avoid bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow group cursor-pointer relative" onClick={() => window.open(note.noteUrl || '#', '_blank')}>
                                      {/* 笔记指标悬浮层 */}
                                      <div className="absolute top-2 right-2 flex flex-col gap-1 z-10 pointer-events-none opacity-90">
                                        <div className="bg-black/50 backdrop-blur text-white text-[9px] px-1.5 py-0.5 rounded-md flex items-center gap-1">
                                          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path></svg>
                                          {note.likeCount || 0}
                                        </div>
                                      </div>
      
                                      <div className="p-3">
                                        <div className="text-xs font-bold text-slate-800 dark:text-slate-200 line-clamp-2 mb-1.5 leading-snug group-hover:text-[#ff2442] transition-colors">
                                          {note.title || '无标题笔记'}
                                        </div>
                                        <div className="text-[10px] text-slate-500 dark:text-slate-400 line-clamp-3 mb-2">
                                          {note.content}
                                        </div>
                                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100 dark:border-slate-700/50">
                                          <div className="flex items-center gap-1.5">
                                            <div className="w-4 h-4 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center overflow-hidden">
                                              <svg className="w-2.5 h-2.5 text-slate-400" fill="currentColor" viewBox="0 0 24 24"><path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                                            </div>
                                            <span className="text-[10px] text-slate-500 dark:text-slate-400 truncate max-w-[80px]">{note.nickname || '小红书用户'}</span>
                                          </div>
                                          {note.timeText && <span className="text-[9px] text-slate-400">{note.timeText}</span>}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              record.sources && record.sources.length > 0 && (
                                <div className="mt-4 pt-3 border-t border-gray-200 dark:border-slate-700">
                                    <p className="text-xs font-bold text-gray-500 dark:text-slate-400 mb-2">引用溯源:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {record.sources.map((src, i) => (
                                            <a key={i} href={src.url} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-2.5 py-1 rounded border border-blue-100 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-800/50 transition-colors text-xs">
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path></svg>
                                                <span>{src.name}</span>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                              )
                            )}


                          </div>
                        </div>
                        
                        {/* New floating-style Zhimo Intervention Action Bar */}
                        {(record.sentiment === 'negative' || record.sentiment === 'controversial' || drawerTitle.includes('负向') || drawerTitle.includes('争议') || drawerTitle.includes('劣势')) && (
                          <div className="flex justify-start pl-2 mt-2 group relative z-10">
                             <button 
                                onClick={() => {
                                  setZhimoCitation(record);
                                  setZhimoSelectedSources(record.sources ? record.sources.map((s, idx) => s.url || s.name || String(idx)) : []);
                                  setIsZhimoDrawerOpen(true);
                                }}
                                className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/60 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-200 dark:hover:bg-indigo-800 transition-colors shadow-sm ring-2 ring-white dark:ring-slate-800 cursor-pointer"
                             >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                             </button>
                             
                             {/* Tooltip on hover */}
                             <div className="absolute left-10 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity bg-indigo-600 text-white text-xs px-2.5 py-1.5 rounded shadow-lg whitespace-nowrap">
                                🛡️ 调起智墨创作舱进行防守干预
                             </div>
                          </div>
                        )}
                        
                    </div>
                 </div>
               ))
            )}
          </div>
        </div>
      </div>


      {/* Media & Article Type Drawer */}
      <div className={`fixed inset-0 z-[60] transition-opacity duration-300 ${isMediaDrawerOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={closeMediaDrawer}></div>
        <div className={`absolute top-0 right-0 h-full w-full sm:w-[1000px] max-w-[95vw] bg-white dark:bg-slate-900 border-l border-gray-200 dark:border-slate-800 shadow-2xl transition-transform duration-300 transform flex flex-col ${isMediaDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex-none flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-800">
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-3">
              {mediaDrawerTitle}
              <span className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400 text-xs px-2 py-1 rounded-full font-medium">
                {mediaDrawerData.length} 条引证
              </span>
            </h2>
            <div className="flex items-center gap-3">
              <button onClick={handleExportMediaExcel} className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors text-sm font-medium shadow-sm">
                <Download className="w-4 h-4" /> 导出 Excel
              </button>
              <button onClick={closeMediaDrawer} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50 dark:bg-[#0f172a]">
            {mediaDrawerLoading ? (
              <div className="flex flex-col items-center justify-center h-48 space-y-4">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                <p className="text-sm text-slate-500 dark:text-slate-400 animate-pulse">正在深度追溯媒体来源...</p>
              </div>
            ) : mediaDrawerData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 space-y-3">
                <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center">
                  <Search className="w-6 h-6 text-slate-400" />
                </div>
                <p className="text-slate-500 dark:text-slate-400">未找到相关溯源数据</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="overflow-x-auto border border-gray-200 dark:border-slate-800 rounded-xl shadow-sm bg-white dark:bg-slate-900">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 dark:bg-slate-800/50 text-gray-600 dark:text-slate-300">
                      <tr>
                        <th className="px-4 py-3 font-medium whitespace-nowrap w-24">来源媒体</th>
                        <th className="px-4 py-3 font-medium whitespace-nowrap w-32">内容类型</th>
                        <th className="px-4 py-3 font-medium whitespace-nowrap w-28">引用引擎</th>
                        <th className="px-4 py-3 font-medium whitespace-nowrap w-32">
                          最终 AI 倾向
                          <span className="inline-block ml-1 text-slate-400 cursor-help" title="指 AI 最终合成回答对该品牌的整体评价，并非单篇文章的基调。中立客观的文章也可能被 AI 用作竞争对比时的负面论据。">
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline-block -mt-0.5"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                          </span>
                        </th>
                        <th className="px-4 py-3 font-medium w-full">标题 / 话题</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-slate-800/60">
                      {mediaDrawerData.map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-slate-800/30 transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-900 dark:text-slate-200 whitespace-nowrap max-w-[120px] truncate" title={row.platform}>
                            {row.platform}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="px-2 py-1 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-md text-xs border border-purple-100 dark:border-purple-800/50">
                              {row.articleType}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                              <Bot className="w-3.5 h-3.5 opacity-70" />
                              {row.engineName}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {row.sentiment === 'positive' && <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>正面</span>}
                            {row.sentiment === 'negative' && <span className="text-rose-600 dark:text-rose-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>负面</span>}
                            {row.sentiment === 'neutral' && <span className="text-slate-500 dark:text-slate-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>中立</span>}
                            {row.sentiment === 'controversial' && <span className="text-amber-500 dark:text-amber-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>争议</span>}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-slate-700 dark:text-slate-300 line-clamp-2" title={row.title}>
                              {row.url !== '#' ? (
                                <a href={row.url} target="_blank" rel="noreferrer" className="hover:text-blue-600 dark:hover:text-blue-400 hover:underline">
                                  {row.title}
                                </a>
                              ) : (
                                row.title
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Zhimo Integration Drawer */}
      {isZhimoDrawerOpen && (
        <div className="fixed inset-0 z-[60] overflow-hidden" aria-labelledby="slide-over-title" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsZhimoDrawerOpen(false)}></div>
          <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <div className="w-screen max-w-md transform transition-all duration-300 ease-in-out">
              <div className="h-full flex flex-col bg-white dark:bg-slate-800 shadow-xl overflow-y-scroll">
                
                <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="p-1.5 bg-indigo-600 text-white rounded-md">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                    </span>
                    <h2 className="text-base font-medium text-gray-900 dark:text-white" id="slide-over-title">智墨 AI 创作舱</h2>
                  </div>
                  <button type="button" className="text-gray-400 hover:text-gray-500 focus:outline-none" onClick={() => setIsZhimoDrawerOpen(false)}>
                    <span className="sr-only">Close panel</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>

                <div className="p-6 flex-1">
                  {/* Selected Citation Info */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">引文来源列表 (请勾选需要重点反驳的负面文章)</h3>
                    {zhimoCitation?.sources && zhimoCitation.sources.length > 0 ? (
                      <div className="space-y-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                        {zhimoCitation.sources.map((source, idx) => {
                          const sourceKey = source.url || source.name || String(idx);
                          const isSelected = zhimoSelectedSources.includes(sourceKey);
                          return (
                            <label key={sourceKey} className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50 border-indigo-200 dark:bg-indigo-900/20 dark:border-indigo-800' : 'bg-gray-50 border-gray-200 dark:bg-slate-900/50 dark:border-slate-700 hover:bg-gray-100 dark:hover:bg-slate-800'}`}>
                              <div className="flex-shrink-0 mt-0.5">
                                <input 
                                  type="checkbox" 
                                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setZhimoSelectedSources([...zhimoSelectedSources, sourceKey]);
                                    } else {
                                      setZhimoSelectedSources(zhimoSelectedSources.filter(k => k !== sourceKey));
                                    }
                                  }}
                                />
                              </div>
                              <div className="ml-3 flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-slate-200 truncate" title={source.name}>{source.name}</p>
                                {source.url && <p className="text-xs text-blue-500 dark:text-blue-400 truncate hover:underline" title={source.url} onClick={(e) => e.stopPropagation()}><a href={source.url} target="_blank" rel="noreferrer">{source.url}</a></p>}
                                {source.snippet && <p className="text-xs text-gray-600 dark:text-slate-400 mt-1 line-clamp-2" title={source.snippet}>{source.snippet}</p>}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-3 bg-gray-50 dark:bg-slate-900/50 rounded-lg border border-gray-200 dark:border-slate-700">
                        <p className="font-medium text-gray-800 dark:text-slate-200 text-sm mb-1">{zhimoCitation?.title || '未知引文来源'}</p>
                        <p className="text-xs text-gray-500 dark:text-slate-400 line-clamp-3">{zhimoCitation?.snippet || (zhimoCitation?.rawResponse ? zhimoCitation.rawResponse.replace(/[#*`_]/g, '').substring(0, 80) + '...' : '暂无内容')}</p>
                      </div>
                    )}
                  </div>

                  {/* Config Options */}
                  {(zhimoCitation ? (zhimoStatuses[zhimoCitation.id] || 'idle') : 'idle') === 'idle' && (
                    <div className="space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">目标分发平台</label>
                        <select 
                          value={zhimoPlatform}
                          onChange={(e) => setZhimoPlatform(e.target.value)}
                          className="w-full rounded-md border-gray-300 dark:border-slate-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white p-2 border"
                        >
                          <option value="GENERAL_NEWS">通用媒体文章 (资讯体 / 应对 DeepSeek、Kimi)</option>
                          <option value="WECHAT">微信公众号 (深度长文 / 应对 腾讯元宝)</option>
                          <option value="XHS">小红书 (图文种草 / 应对 小红书AI)</option>
                          <option value="TOUTIAO">今日头条 (话题资讯 / 应对 豆包)</option>
                          <option value="ZHIHU">知乎 (深度问答 / 应对 综合搜索与AI引擎)</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">创作语调设置</label>
                        <select value={zhimoTone} onChange={(e) => setZhimoTone(e.target.value)} className="w-full rounded-md border-gray-300 dark:border-slate-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white p-2 border">
                          <option value="行业专家科普 (客观中立、数据支撑)">行业专家科普 (客观中立 / 适配: 资讯、知乎)</option>
                          <option value="真实用户分享 (第一人称、提供情绪价值)">真实用户分享 (第一人称 / 适配: 小红书、头条)</option>
                          <option value="硬核测评对比 (横向评测、直击痛点)">硬核测评对比 (痛点对比 / 适配: 微信、知乎)</option>
                          <option value="公关澄清辟谣 (权威定调、纠正偏见事实)">公关澄清辟谣 (权威发声 / 适配: 应对恶性谣言)</option>
                        </select>
                      </div>

                      <button 
                        onClick={async () => {
                          setZhimoStatuses(prev => ({...prev, [zhimoCitation?.id]: 'generating'}));
                          try {
                              // Find the primary brand name from competitorTable if not explicitly provided
                              let actualBrandName = '核心品牌';
                              if (dashboardData?.competitorTable && dashboardData.competitorTable.length > 0) {
                                  // In BrandInsights, the first row of competitorTable usually represents the main brand
                                  actualBrandName = dashboardData.competitorTable[0].name;
                              }
                              const brandName = dashboardData?.brandName || actualBrandName;
                              const res = await fetch('/api/zhimo/generate-sync', {
                                  method: 'POST',
                                  headers: {
                                      'Content-Type': 'application/json',
                                      'Authorization': 'Bearer ' + localStorage.getItem('token')
                                  },
                                  body: JSON.stringify({
                                      sourceText: (() => {
                                          const selectedSourcesList = zhimoCitation?.sources 
                                              ? zhimoCitation.sources.filter((s, idx) => zhimoSelectedSources.includes(s.url || s.name || String(idx)))
                                              : [];
                                          const sourcesText = selectedSourcesList.length > 0 
                                              ? selectedSourcesList.map(s => `- ${s.name} (${s.url || '未知链接'})\n  内容摘要: ${s.snippet || '无'}`).join('\n\n') 
                                              : (zhimoCitation?.sources?.map(s => '- ' + s.name + ' (' + s.url + ')').join('\n') || '无');
                                          return `【触发问题】: ${zhimoCitation?.question || '未知'}\n\n【AI生成的争议原文】: \n${zhimoCitation?.rawResponse || '无'}\n\n【我们重点关注并需要反驳的负面参考文章】: \n${sourcesText}`;
                                      })(),
                                      brand: brandName,
                                      targetPlatform: zhimoPlatform,
                                      tone: zhimoTone,
                                      title: zhimoCitation?.question ? zhimoCitation.question.substring(0, 20) + '...' : '负面狙击干预任务'
                                  })
                              });
                              const data = await res.json();
                              if (data.success) {
                                  setZhimoResults(prev => ({...prev, [zhimoCitation?.id]: data.content}));
                                  setZhimoStatuses(prev => ({...prev, [zhimoCitation?.id]: 'completed'}));
                              } else {
                                  alert('生成失败: ' + data.error);
                                  setZhimoStatuses(prev => ({...prev, [zhimoCitation?.id]: 'idle'}));
                              }
                          } catch (err) { // eslint-disable-next-line no-unused-vars
                              alert('网络错误');
                              setZhimoStatuses(prev => ({...prev, [zhimoCitation?.id]: 'idle'}));
                          }
                        }}
                        className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        🚀 确认投递，开始智能重塑
                      </button>
                    </div>
                  )}

                  {/* Progress Status */}
                  {(zhimoCitation ? (zhimoStatuses[zhimoCitation.id] || 'idle') : 'idle') === 'generating' && (
                    <div className="py-10 flex flex-col items-center justify-center text-center space-y-4">
                       <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                       <div>
                         <p className="text-gray-900 dark:text-white font-medium">AI 正在深度解析引文并重写...</p>
                         <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">预计需要 15-30 秒，请稍候</p>
                       </div>
                    </div>
                  )}

                  {/* Completed Status */}
                  {(zhimoCitation ? (zhimoStatuses[zhimoCitation.id] || 'idle') : 'idle') === 'completed' && (
                    <div className="py-6 flex flex-col items-center text-center space-y-4">
                       <div className="w-16 h-16 bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mb-2">
                         <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                       </div>
                       <div>
                         <h4 className="text-base font-medium text-gray-900 dark:text-white mb-1">内容重塑完成！</h4>
                         <p className="text-sm text-gray-500 dark:text-slate-400">已成功生成一篇符合 {zhimoPlatform} 风格的新文章。</p>
                       </div>
                       <div className="w-full p-4 bg-gray-50 dark:bg-slate-900 rounded-md text-left mt-4 border border-gray-100 dark:border-slate-700 text-sm text-gray-700 dark:text-slate-300 max-h-64 overflow-y-auto whitespace-pre-wrap">
                         {zhimoResults[zhimoCitation?.id] || '未能生成有效内容'}
                       </div>
                       
                       <div className="flex w-full gap-3 mt-4">
                         <button 
                           onClick={() => setZhimoStatuses(prev => ({...prev, [zhimoCitation?.id]: 'idle'}))}
                           className="flex-1 flex justify-center py-2 px-4 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700">
                           🔄 不满意，重新生成
                         </button>
                         <button 
                           onClick={() => {
                             if (navigator.clipboard && window.isSecureContext) {
                               navigator.clipboard.writeText(zhimoResults[zhimoCitation?.id] || '')
                                 .then(() => alert('已成功复制到剪贴板！'))
                                 .catch(err => alert('复制失败，请尝试手动选中复制。' + err));
                             } else {
                               // Fallback for non-HTTPS or unsupported browsers
                               const textArea = document.createElement("textarea");
                               textArea.value = zhimoResults[zhimoCitation?.id] || '';
                               document.body.appendChild(textArea);
                               textArea.focus();
                               textArea.select();
                               try {
                                 document.execCommand('copy');
                                 alert('已成功复制到剪贴板！');
                               } catch (err) {
                                 alert('复制失败，请尝试手动选中复制。');
                               }
                               document.body.removeChild(textArea);
                             }
                           }}
                           className="flex-1 flex justify-center py-2 px-4 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700">
                           📋 复制全文
                         </button>
                       </div>
                    </div>
                  )}
                </div>

                {/* Footer - SSO Upsell */}
                <div className="p-4 bg-gray-50 dark:bg-slate-900 border-t border-gray-200 dark:border-slate-700 text-center">
                  <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">想要更强大的图文混排和全域分发能力？</p>
                  <button 
                    onClick={() => { 
                      // Use the current hostname but point to Zhimo's port (3000)
                      const currentUserStr = localStorage.getItem('user'); const currentUserId = currentUserStr ? JSON.parse(currentUserStr).id : 1; const zhimoUrl = 'https://www.sapientink.com/content/articles?sso_user_id=' + currentUserId;
                      window.open(zhimoUrl, '_blank'); 
                    }}
                    className="w-full flex items-center justify-center gap-2 py-2.5 px-4 border border-indigo-200 dark:border-indigo-800 rounded-md shadow-sm text-sm font-medium text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                  >
                    🌟 进入智墨全域创作台
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg>
                  </button>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

    </div>
    </ErrorBoundary>
  );
}